import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';

export default function ContactPage() {
  const { settings } = useSiteSettings();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.querySelector('[name="message"]') as HTMLTextAreaElement)?.value || '';
    if (msg.length > 500) return;
    setStatus('sending');

    const data = new URLSearchParams();
    new FormData(form).forEach((val, key) => data.append(key, val as string));

    try {
      const res = await fetch('https://readdy.ai/api/form/d71i31s991ove783t07g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('sent');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const CONTACT_INFO = [
    { icon: 'ri-map-pin-2-line', labelKey: 'contact.info.address', value: settings.companyAddress },
    { icon: 'ri-phone-line', labelKey: 'contact.info.phone', value: settings.companyPhone },
    { icon: 'ri-mail-line', labelKey: 'contact.info.email', value: settings.companyEmail },
    { icon: 'ri-time-line', labelKey: 'contact.info.hours', valueKey: 'contact.info.hoursVal' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div
        className="pt-28 pb-16 text-white"
        style={{ background: 'linear-gradient(135deg, #0d2b2b 0%, var(--primary) 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-heading font-bold text-4xl md:text-5xl mb-4">{t('contact.title')}</h1>
          <p className="text-white/70 text-lg">{t('contact.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                {t('contact.info.title')}
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t('contact.info.desc')}
              </p>
            </div>

            {CONTACT_INFO.map(({ icon, labelKey, value, valueKey }) => (
              <div key={labelKey} className="flex items-start gap-4">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--primary-light)' }}
                >
                  <i className={`${icon} text-lg`} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold tracking-wide uppercase mb-0.5">{t(labelKey)}</div>
                  <div className="text-gray-900 text-sm font-medium">{valueKey ? t(valueKey) : value}</div>
                </div>
              </div>
            ))}

            {/* Map embed */}
            <div className="rounded-2xl overflow-hidden" style={{ height: '200px' }}>
              <iframe
                title="Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d214600.0!2d-96.7969879!3d32.7767!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e991853200d29%3A0xfbc0bd8a87a4e99!2sDallas%2C%20TX!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div
              className="bg-white rounded-2xl p-8 border border-gray-100"
              style={{ boxShadow: '0 4px 24px rgba(13,148,136,0.08)' }}
            >
              {status === 'sent' ? (
                <div className="text-center py-16">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                    style={{ background: 'var(--primary-light)' }}
                  >
                    <i className="ri-check-line text-3xl" style={{ color: 'var(--primary)' }} />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">{t('contact.sent.title')}</h3>
                  <p className="text-gray-500 text-sm">{t('contact.sent.desc')}</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 px-6 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer"
                    style={{ background: 'var(--primary)' }}
                  >
                    {t('contact.sent.another')}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  id="contact-form"
                  data-readdy-form
                  className="space-y-5"
                >
                  <h3 className="font-heading font-semibold text-xl text-gray-900 mb-6">{t('contact.form.title')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{t('contact.form.name')}</label>
                      <input
                        name="full_name"
                        type="text"
                        required
                        placeholder="John Smith"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 outline-none transition-colors placeholder-gray-400"
                        onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                        onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{t('contact.form.email')}</label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="john@company.com"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 outline-none transition-colors placeholder-gray-400"
                        onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                        onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{t('contact.form.phone')}</label>
                      <input
                        name="phone"
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 outline-none transition-colors placeholder-gray-400"
                        onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                        onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{t('contact.form.subject')}</label>
                      <select
                        name="subject"
                        required
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 outline-none transition-colors cursor-pointer"
                        onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                        onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                      >
                        <option value="">{t('contact.form.subjectPlaceholder')}</option>
                        <option value="track">{t('contact.form.subjectTrack')}</option>
                        <option value="quote">{t('contact.form.subjectQuote')}</option>
                        <option value="enterprise">{t('contact.form.subjectEnterprise')}</option>
                        <option value="billing">{t('contact.form.subjectBilling')}</option>
                        <option value="other">{t('contact.form.subjectOther')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                      {t('contact.form.message')} <span className="text-gray-400 font-normal normal-case">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="..."
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 outline-none resize-none transition-colors placeholder-gray-400"
                      onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                      onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                    />
                    {charCount > 500 && (
                      <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                    )}
                  </div>

                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-red-600">
                      {t('contact.form.error')}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || charCount > 500}
                    className="w-full py-3.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60 cursor-pointer"
                    style={{ background: 'var(--primary)' }}
                  >
                    {status === 'sending' ? t('contact.form.sending') : t('contact.form.send')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
